<template>
  <div v-if="!isDisabledRole">
    <RfLinkedSegments
      v-if="!isDisabledByActiveExperiment"
      ref="linkedSegments"
      :model="prompt"
      @confirm="updateSegments"
    />

    <RfBaseModal
      v-if="!isDisabledByActiveExperiment"
      ref="triggersModal"
      title="Triggers"
      width="1200"
      @primary="updateTriggers"
    >
      <template #body>
        <RfTrigger ref="triggers" :model="prompt" :triggerType="triggerType" />
      </template>
    </RfBaseModal>

    <RfTraffic
      v-if="!isDisabledByActiveExperiment && prompt?.filter"
      ref="traffic"
      :model="prompt"
      @confirm="updateLimits"
    />

    <RfBaseModal
      ref="scheduleModal"
      title="When should it be shown"
      width="750"
      @primary="updateSchedule"
    >
      <template #body>
        <div class="flex flex-col gap-3">
          <RfButtonTabs :active="scheduleType">
            <RfButtonNew
              v-for="(name, i) in scheduleTabs"
              :key="name"
              type="plain"
              size="large"
              class="[&_.rf-button--container]:text-beige-90 px-4 py-2 capitalize"
              :class="{
                '[&_.rf-button--container]:font-semibold!': i === scheduleType,
                'hidden!': scheduleTabs.length === 1,
              }"
              @click="scheduleType = i"
            >
              {{ name }}
            </RfButtonNew>
          </RfButtonTabs>
          <RfSchedule
            ref="schedule"
            :model="prompt"
            :isPushNotification="isPushNotification"
            :scheduleType="scheduleTabs[scheduleType]"
          />
        </div>
      </template>
    </RfBaseModal>

    <RfBaseModal
      v-if="!isDisabledByActiveExperiment"
      ref="intervalModal"
      title="Change interval"
      width="600"
      @primary="updateInterval"
      @show="
        () => {
          permanentDisplay = props.prompt.actions.rf_settings_permanent_display;
          acceptInterval = props.prompt.actions.rf_settings_accept_interval;
        }
      "
    >
      <template #body>
        <div class="flex flex-col">
          <v-radio-group
            v-model="permanentDisplay"
            class="mt-0 pt-0"
            hideDetails
            inline
            density="compact"
          >
            <v-radio value="false" label="Show based on timer" />
            <v-radio value="true" label="Show Repeatedly" />
          </v-radio-group>
          <RfPromoInteractionInterval
            v-if="`${permanentDisplay}` === 'false'"
            :key="acceptInterval"
            interactionType="accept"
            :action="acceptInterval"
            @setIntervalAction="v => (acceptInterval = v)"
          />
        </div>
      </template>
    </RfBaseModal>

    <RfBaseModal
      v-if="!isDisabledByActiveExperiment"
      ref="metadataModal"
      title="Set Metadata"
      width="600"
      @primary="updateMetadata"
    >
      <template #body>
        <RfRetentionActionsMetadata :actions="metadataAction" />
      </template>
    </RfBaseModal>

    <RfEngagementCategoryModal
      ref="engagementCategoryModal"
      kind="prompt"
      :item="prompt"
      @save="updateEngagementCategory"
    />

    <RfBaseModal
      v-if="!isDisabledByActiveExperiment"
      ref="consentCategoriesModal"
      title="Edit Consent Categories"
      width="600"
      @primary="updateConsentCategories"
      @show="
        selectedConsentCategories = (props.prompt.consent_categories || []).filter(c =>
          visibleConsentCategories.some(v => v.value === c),
        )
      "
    >
      <template #body>
        <div class="flex flex-col gap-2">
          <RfCheckbox
            v-for="cat in visibleConsentCategories"
            :key="cat.value"
            size="small"
            :modelValue="selectedConsentCategories.includes(cat.value)"
            @update:modelValue="checked => toggleCategory(cat.value, checked)"
          >
            <span class="flex flex-col">
              <span>{{ cat.label }}</span>
              <span class="text-xs text-gray-500">{{ cat.description }}</span>
            </span>
          </RfCheckbox>
        </div>
      </template>
    </RfBaseModal>

    <RfCustomDevicesModal ref="rfCustomDevicesModal" />
  </div>
</template>

<script setup>
import RfLinkedSegments from "@/components/RfPathCreate/RfLinkedSegments.vue";
import RfSchedule from "@/components/RfPathCreate/RfSchedule.vue";
import RfTraffic from "@/components/RfPathCreate/RfTraffic.vue";
import RfTrigger from "@/components/RfPathCreate/RfTrigger.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import { cloneDeep } from "lodash-es";
import { computed, ref } from "vue";
import RfPromoInteractionInterval from "@/components/RfPromotions/RfPromoInteractionInterval.vue";
import RfCustomDevicesModal from "@/components/modals/RfCustomDevicesModal.vue";
import RfRetentionActionsMetadata from "@/components/RfPathCreate/RfRetentionActions/RfRetentionActionsMetadata.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfButtonTabs from "@/components/buttons/RfButtonTabs.vue";
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import RfEngagementCategoryModal from "@/blocks/RfModals/RfEngagementCategoryModal.vue";
import { useConsentCategories } from "@/utils/composables/useConsentCategories";

const emit = defineEmits(["updateTriggers", "updatePrompt", "updateSegments"]);
const props = defineProps({
  prompt: Object,
  isDisabledRole: [Boolean, String],
  isDisabledByActiveExperiment: [Boolean, String],
  triggerType: String,
  isPushNotification: { type: Boolean, default: false },
});

const { consentCategories } = useConsentCategories();
const visibleConsentCategories = computed(() =>
  props.prompt?.device_type === "web"
    ? consentCategories.value.filter(c => c.value === "performance")
    : consentCategories.value,
);

const linkedSegments = ref();
const triggersModal = ref();
const triggers = ref();
const traffic = ref();
const scheduleModal = ref();
const intervalModal = ref();
const schedule = ref();
const rfCustomDevicesModal = ref();
const metadataModal = ref();
const engagementCategoryModal = ref();
const consentCategoriesModal = ref();
const metadataAction = ref({ rf_metadata: "" });
const selectedConsentCategories = ref([]);
const acceptInterval = ref(props.prompt.actions?.rf_settings_accept_interval);
const permanentDisplay = ref(props.prompt.actions?.rf_settings_permanent_display);
const scheduleType = ref(0);
const scheduleTabs = computed(() =>
  props.isPushNotification ? ["schedule"] : ["schedule", "daypart"],
);
const updateSegments = newPath => {
  emit("updateSegments", newPath);
};

const updateTriggers = () => {
  const newPath = cloneDeep(props.prompt);
  triggers.value.fillModel(newPath);
  emit("updateTriggers", { newPath });
};

const updateLimits = newPath => {
  emit("updatePrompt", newPath);
};

const updateSchedule = () => {
  const newPath = cloneDeep(props.prompt);
  schedule.value.fillModel(newPath);
  emit("updatePrompt", newPath);
};

const updateInterval = () => {
  const newPath = cloneDeep(props.prompt);
  newPath.actions.rf_settings_permanent_display = permanentDisplay.value;
  newPath.actions.rf_settings_accept_interval = acceptInterval.value;
  emit("updatePrompt", newPath);
};

const updateMetadata = () => {
  const newPath = cloneDeep(props.prompt);
  newPath.actions.rf_metadata = metadataAction.value.rf_metadata;
  emit("updatePrompt", newPath);
};

const updateEngagementCategory = (category, subcategory) => {
  const newPath = cloneDeep(props.prompt);
  newPath.engagement_category = category;
  // Subcategory of in-sequence prompts is owned by the sequence — never set it here
  if (!props.prompt?.sequence_id) newPath.engagement_subcategory = subcategory;
  emit("updatePrompt", newPath);
};

const updateConsentCategories = () => {
  const cats = [...selectedConsentCategories.value];
  const newPath = cloneDeep(props.prompt);
  newPath.consent_categories = cats;
  if (newPath.filter) newPath.filter.consent_categories = cats;
  emit("updatePrompt", newPath);
};

const toggleCategory = (value, checked) => {
  selectedConsentCategories.value = checked
    ? [...selectedConsentCategories.value, value]
    : selectedConsentCategories.value.filter(v => v !== value);
};

const showSegmentsModal = () => linkedSegments.value?.open();
const closeSegmentsModal = () => linkedSegments.value?.close();

const showTriggersModal = () => triggersModal.value?.show();
const closeTriggersModal = () => triggersModal.value?.close();

const showLimitsModal = () => traffic.value?.open();
const closeLimitsModal = () => traffic.value?.close();

const showScheduleModal = () => scheduleModal.value?.show();
const closeScheduleModal = () => scheduleModal.value?.close();

const showIntervalModal = () => intervalModal.value?.show();
const closeIntervalModal = () => intervalModal.value?.close();

const showCustomDevicesModal = () => rfCustomDevicesModal.value?.show();
const closeCustomDevicesModal = () => rfCustomDevicesModal.value?.close();

const showMetadataModal = () => {
  metadataAction.value = { rf_metadata: props.prompt.actions?.rf_metadata || "" };
  metadataModal.value?.show();
};
const closeMetadataModal = () => metadataModal.value?.close();

const showEngagementCategoryModal = () => engagementCategoryModal.value?.show();
const closeEngagementCategoryModal = () => engagementCategoryModal.value?.close();

const showConsentCategoriesModal = () => consentCategoriesModal.value?.show();
const closeConsentCategoriesModal = () => consentCategoriesModal.value?.close();

defineExpose({
  showSegmentsModal,
  closeSegmentsModal,
  showTriggersModal,
  closeTriggersModal,
  showLimitsModal,
  closeLimitsModal,
  showScheduleModal,
  closeScheduleModal,
  showIntervalModal,
  closeIntervalModal,
  showCustomDevicesModal,
  closeCustomDevicesModal,
  showMetadataModal,
  closeMetadataModal,
  showEngagementCategoryModal,
  closeEngagementCategoryModal,
  showConsentCategoriesModal,
  closeConsentCategoriesModal,
});
</script>
